
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatEventType } from "@/utils/securityUtils";
import { getSeverityColor } from "@/utils/securityUtils";
import { getSeverityIcon } from "./SecuritySummaryCard";
import { useNavigate } from "react-router-dom";
import { MoreVertical, Trash } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface SecurityEvent {
  id: string;
  created_at: string;
  event_type: string;
  severity: string;
  ip_address: string | null;
  user_agent: string | null;
  event_details: any;
  resolved: boolean;
  resolution_notes?: string;
  action_taken?: string;
  user_email?: string;
}

interface SecurityEventsTableProps {
  events: SecurityEvent[];
  isLoading: boolean;
}

export const SecurityEventsTable = ({ events, isLoading }: SecurityEventsTableProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (eventId: string) => {
    if (!confirm("Are you sure you want to delete this security event? This action cannot be undone.")) {
      return;
    }

    setDeletingId(eventId);
    try {
      const { error } = await supabase
        .from('security_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: "Event deleted",
        description: "The security event has been successfully deleted.",
      });

      // Refresh the page to update the list
      window.location.reload();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete the security event. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Time</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Severity</TableHead>
          <TableHead>IP Address</TableHead>
          <TableHead>Status</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events?.map((event) => (
          <TableRow key={event.id}>
            <TableCell>
              {new Date(event.created_at).toLocaleString()}
            </TableCell>
            <TableCell>
              {formatEventType(event.event_type)}
            </TableCell>
            <TableCell>
              <span className={`flex items-center gap-2 ${getSeverityColor(event.severity)}`}>
                {getSeverityIcon(event.severity)}
                {event.severity}
              </span>
            </TableCell>
            <TableCell>{event.ip_address}</TableCell>
            <TableCell>
              <span className={`px-2 py-1 rounded-full text-xs ${
                event.resolved 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {event.resolved ? 'Resolved' : 'Unresolved'}
              </span>
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" disabled={deletingId === event.id}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/admin/security/event/${event.id}`)}>
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/admin/security/event/${event.id}/edit`)}>
                    Update Status
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleDelete(event.id)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Delete Event
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
